import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import ReactMarkdown from "react-markdown";
import { Send, Loader2, ExternalLink, Copy, Check, SearchX } from "lucide-react";
import { useLang } from "@/context/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function StrategistChat({ initialQuestion }) {
  const { lang, setLang, dir, t, suggestions } = useLang();
  const [q, setQ] = useState("");
  const [msgs, setMsgs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [copiedIdx, setCopiedIdx] = useState(null);
  const scrollRef = useRef(null);
  const sentInitial = useRef(false);

  const send = useCallback(async (preset) => {
    const question = (typeof preset === "string" ? preset : q).trim();
    if (!question || loading) return;
    setQ(""); setError(null); setLoading(true);
    setMsgs((m) => [...m, { role: "user", text: question }]);
    try {
      const { data } = await axios.post(`${API}/rag/query`, { question, uiLanguage: lang });
      if (data.detectedLanguage) setLang(data.detectedLanguage);
      setMsgs((m) => [...m, { role: "bot", ...data }]);
    } catch (e) {
      setError(t("common.networkError"));
    } finally { setLoading(false); }
  }, [q, loading, lang, setLang, t]);

  useEffect(() => {
    if (initialQuestion && !sentInitial.current) {
      sentInitial.current = true;
      send(initialQuestion);
    }
  }, [initialQuestion, send]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [msgs, loading]);

  const copyAnswer = async (text, i) => {
    const clean = (text || "")
      .replace(/^#{1,6}\s*/gm, "").replace(/\*\*(.*?)\*\*/g, "$1")
      .replace(/^\s*[-*]\s+/gm, "• ").replace(/\[(\d+)\]/g, "").trim();
    try { await navigator.clipboard.writeText(clean); } catch (e) { /* ignore */ }
    setCopiedIdx(i); setTimeout(() => setCopiedIdx((c) => (c === i ? null : c)), 1800);
  };

  return (
    <div className="es-chat" data-testid="chat-panel" dir={dir}>
      <div className="es-chat-scroll" ref={scrollRef}>
        {msgs.length === 0 && !loading && (
          <div className="es-chat-empty" data-testid="chat-empty">
            <p className="es-chat-intro">{t("chat.intro")}</p>
          </div>
        )}
        {msgs.map((m, i) => m.role === "user" ? (
          <div key={i} className="es-msg-user" data-testid="chat-user-msg">{m.text}</div>
        ) : (
          <div key={i} className="es-msg-bot" data-testid="chat-bot-msg">
            {!m.grounded ? (
              <div className="es-notfound" data-testid="chat-notfound">
                <SearchX size={16} />
                <div className="es-markdown"><ReactMarkdown>{m.answer}</ReactMarkdown></div>
              </div>
            ) : (
              <>
                <div className="es-markdown" data-testid="chat-answer">
                  <ReactMarkdown>{m.answer}</ReactMarkdown>
                </div>
                <button className="es-copy" data-testid={`chat-copy-${i}`}
                  onClick={() => copyAnswer(m.answer, i)}>
                  {copiedIdx === i ? <><Check size={13} /> {t("chat.copied")}</> : <><Copy size={13} /> {t("chat.copy")}</>}
                </button>
              </>
            )}
            {m.sources?.length > 0 && (
              <div className="es-sources" data-testid="chat-sources">
                <div className="es-sources-label">{t("chat.sources")}</div>
                {m.sources.map((s, j) => (
                  <a key={j} className="es-source" href={s.sourceUrl} target="_blank" rel="noreferrer" data-testid="chat-source">
                    <span className="es-badge">{s.language}</span>
                    <span className="es-source-title">{s.title}</span>
                    <ExternalLink size={12} />
                  </a>
                ))}
              </div>
            )}
          </div>
        ))}
        {loading && (
          <div className="es-msg-bot es-loading" data-testid="chat-loading">
            <Loader2 className="es-spin" size={16} /> {t("chat.searching")}
          </div>
        )}
        {error && <div className="es-chat-error" data-testid="chat-error">{t("chat.error")}: {error}</div>}
      </div>

      {msgs.length === 0 && !loading && (
        <div className="es-suggestions" data-testid="chat-suggestions" dir={dir}>
          {suggestions.map((s, i) => (
            <button key={`${lang}-${i}`} className="es-chip" data-testid={`chat-suggestion-${i}`}
              onClick={() => send(s.q)} disabled={loading}>
              <span className="es-chip-topic">{s.topic}</span> {s.q}
            </button>
          ))}
        </div>
      )}

      <div className="es-chat-input" data-testid="chat-input-row" dir={dir}>
        <input data-testid="chat-input" value={q} placeholder={t("chat.placeholder")}
          aria-label={t("chat.placeholder")}
          onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} />
        <button className="es-send" data-testid="chat-send" onClick={() => send()} disabled={loading}
          aria-label="Send">
          {loading ? <Loader2 className="es-spin" size={18} /> : <Send size={18} />}
        </button>
      </div>
    </div>
  );
}
